import { Plus } from "lucide-react";
import { useState } from "react";

import type { ScorecardDisplayCriterion } from "../../../../../../../stores";
import ScorecardSectionBlockHeader from "../Detail/ScorecardSectionBlockHeader";
import { SCORECARD_EVALUATOR_GRID } from "../../constants";
import { describeCriterionSummaryIssue, isAggregateCriterionShape } from "../../utils";

import ScorecardGuideCard from "./ScorecardGuideCard";
import ScorecardCriterionErrorsBanner, {
  type ScorecardCriterionIssue,
} from "./ScorecardCriterionErrorsBanner";
import ScorecardCriterionRow from "./ScorecardCriterionRow";

import { ScoreVerdict } from "@/queries/smart-tables/scorecard";
import type { SmartColumn } from "@/queries/smart-tables/types";

interface ScorecardOverviewViewProps {
  displayCriteria: ScorecardDisplayCriterion[];
  columns: SmartColumn[];
  isRerunDisabled: boolean;
  getIsCriterionRerunning: (stepId: string) => boolean;
  getIsCriterionStale: (stepId: string) => boolean;
  onRerunCriterion: (stepId: string) => void;
  onOpenFailedRows: (stepId: string, filter?: "fail" | "error") => void;
  onOpenCriterion: (stepId: string) => void;
  onEditCriterion: (stepId: string) => void;
  onAddRowCriterion: () => void;
  isReadOnly?: boolean;
}

const SectionAddButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-1.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/30 hover:text-foreground"
  >
    <Plus className="h-3 w-3" />
    {label}
  </button>
);

const RemovedCriteriaToggle = ({
  count,
  isExpanded,
  onToggle,
}: {
  count: number;
  isExpanded: boolean;
  onToggle: () => void;
}) => (
  <button
    type="button"
    onClick={onToggle}
    className="inline-flex items-center px-1.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground"
  >
    {isExpanded ? "Hide removed" : `Show ${count} removed`}
  </button>
);

const ScorecardOverviewView = ({
  displayCriteria,
  columns,
  isRerunDisabled,
  getIsCriterionRerunning,
  getIsCriterionStale,
  onRerunCriterion,
  onOpenFailedRows,
  onOpenCriterion,
  onEditCriterion,
  onAddRowCriterion,
  isReadOnly = false,
}: ScorecardOverviewViewProps) => {
  const [isRowRemovedExpanded, setIsRowRemovedExpanded] = useState(false);

  const evaluatorCriteria = displayCriteria;
  const activeEvaluatorCriteria = evaluatorCriteria.filter(item => !item.isRemoved);
  const removedEvaluatorCriteria = evaluatorCriteria.filter(item => item.isRemoved);
  const hasNoActiveCriteria = evaluatorCriteria.every(item => item.isRemoved);
  const rowCriteriaLabel =
    evaluatorCriteria.length > 0 ? `Evaluators · ${activeEvaluatorCriteria.length}` : "Evaluators";
  const criteriaNeedingAttention = evaluatorCriteria.reduce<ScorecardCriterionIssue[]>(
    (issues, item) => {
      if (isAggregateCriterionShape(item)) {
        return issues;
      }
      if (!item.summary || item.isRemoved) {
        return issues;
      }

      const reason = describeCriterionSummaryIssue(item.summary, item.step?.thresholds);
      if (reason) {
        issues.push({
          stepId: item.stepId,
          title: item.title ?? item.summary.title ?? item.step?.title ?? "Evaluator",
          verdict: item.summary.verdict,
          reason,
        });
      }
      return issues;
    },
    [],
  );
  const renderCriterionRows = (criteria: ScorecardDisplayCriterion[]) =>
    criteria.map(item => (
      <ScorecardCriterionRow
        key={item.stepId}
        step={item.step}
        summary={item.summary}
        isRemoved={item.isRemoved}
        primitiveType={item.primitiveType}
        titleOverride={item.title}
        columns={columns}
        onOpen={item.isRemoved ? undefined : () => onOpenCriterion(item.stepId)}
        onEdit={item.isRemoved || isReadOnly ? undefined : () => onEditCriterion(item.stepId)}
        onRerun={item.isRemoved || isReadOnly ? undefined : () => onRerunCriterion(item.stepId)}
        isRerunning={getIsCriterionRerunning(item.stepId)}
        isRerunDisabled={isRerunDisabled}
        isStale={getIsCriterionStale(item.stepId)}
        displayMode="card"
      />
    ));

  if (hasNoActiveCriteria) {
    return (
      <div className="space-y-3">
        <ScorecardGuideCard
          primaryActionLabel="Create first evaluator"
          onPrimaryAction={isReadOnly ? undefined : onAddRowCriterion}
        />

        {removedEvaluatorCriteria.length > 0 ? (
          <>
            <RemovedCriteriaToggle
              count={removedEvaluatorCriteria.length}
              isExpanded={isRowRemovedExpanded}
              onToggle={() => setIsRowRemovedExpanded(value => !value)}
            />
            {isRowRemovedExpanded ? (
              <div className={SCORECARD_EVALUATOR_GRID}>
                {renderCriterionRows(removedEvaluatorCriteria)}
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <section className="space-y-2">
        <ScorecardSectionBlockHeader
          label={rowCriteriaLabel}
          description="Define how each row should be scored."
          action={
            evaluatorCriteria.length > 0 && !isReadOnly ? (
              <SectionAddButton label="Add evaluator" onClick={onAddRowCriterion} />
            ) : undefined
          }
        />

        <ScorecardCriterionErrorsBanner
          issues={criteriaNeedingAttention}
          onOpenCriterion={onOpenCriterion}
          onOpenIssue={issue =>
            onOpenFailedRows(issue.stepId, issue.verdict === ScoreVerdict.ERROR ? "error" : "fail")
          }
        />

        <div className={SCORECARD_EVALUATOR_GRID}>
          {renderCriterionRows(activeEvaluatorCriteria)}
        </div>

        {removedEvaluatorCriteria.length > 0 ? (
          <>
            <RemovedCriteriaToggle
              count={removedEvaluatorCriteria.length}
              isExpanded={isRowRemovedExpanded}
              onToggle={() => setIsRowRemovedExpanded(value => !value)}
            />
            {isRowRemovedExpanded ? (
              <div className={SCORECARD_EVALUATOR_GRID}>
                {renderCriterionRows(removedEvaluatorCriteria)}
              </div>
            ) : null}
          </>
        ) : null}
      </section>
    </div>
  );
};

export default ScorecardOverviewView;
